import { useState } from "react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, X, Upload, Loader2, GripVertical } from "lucide-react";

export function FileUploadField({ label, value, onChange, accept = "image/*", isPrivate = false, preview = true }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/upload?private=${isPrivate}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };
  const isImg = accept.includes("image");
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="flex items-center gap-3 mt-1">
        {preview && value && isImg && <img src={fileUrl(value)} alt="" className="h-14 w-14 rounded-lg object-cover border" />}
        {preview && value && !isImg && <span className="text-xs text-secondary truncate max-w-[140px]">✓ {value.split("/").pop()}</span>}
        <label className="cursor-pointer">
          <input type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          <span className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {value ? "Replace" : "Upload"}
          </span>
        </label>
        {value && <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={() => onChange(null)}>Remove</Button>}
      </div>
    </div>
  );
}

export function ListEditor({ label, items = [], onChange, placeholder = "Add item" }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } };
  return (
    <div>
      {label && <Label>{label}</Label>}
      <div className="flex gap-2 mt-1">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Button type="button" variant="outline" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm">
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}><X className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PickupEditor({ items = [], onChange }) {
  const [name, setName] = useState("");
  const [extra, setExtra] = useState("");
  const add = () => {
    if (!name.trim()) return;
    onChange([...items, { name: name.trim(), extra: Number(extra) || 0 }]);
    setName(""); setExtra("");
  };
  return (
    <div data-testid="pickup-editor">
      <Label>Pickup locations & price difference (₹ per person, 0 = base price)</Label>
      <div className="flex gap-2 mt-1">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Delhi (Kashmere Gate)" className="flex-1" data-testid="pickup-name"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <Input type="number" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="+ ₹" className="w-28" data-testid="pickup-extra" />
        <Button type="button" variant="outline" onClick={add} data-testid="pickup-add"><Plus className="h-4 w-4" /></Button>
      </div>
      <div className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2 rounded-md bg-muted px-3 py-1.5 text-sm" data-testid="pickup-item">
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="flex-1">{it.name}</span>
            <Input type="number" value={it.extra} onChange={(e) => onChange(items.map((x, j) => j === i ? { ...x, extra: Number(e.target.value) || 0 } : x))} className="h-7 w-24 text-xs" />
            <span className="text-xs text-muted-foreground w-16">{it.extra > 0 ? `+₹${it.extra}` : it.extra < 0 ? `−₹${Math.abs(it.extra)}` : "base"}</span>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))}><X className="h-4 w-4 text-muted-foreground hover:text-destructive" /></button>
          </div>
        ))}
        {!items.length && <p className="text-xs text-muted-foreground">No pickups yet — travellers won't be asked to choose one.</p>}
      </div>
    </div>
  );
}
